import React from 'react';
import styled from 'styled-components';
import type { AccountState } from './accountState';
import { homeAccountSlot } from './accountState';
import { V4AccountAvatar } from './V4AccountAvatar';
import { V4AccountMenu } from './V4AccountMenu';
import type { AccountMenuSurface } from './useAccountMenuSurface';

const Slot = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const SignInButton = styled.button`
  border: 0;
  background: none;
  padding: 8px 10px;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 15px;
  color: hsl(var(--foreground));
  cursor: pointer;
  &:hover {
    color: hsl(var(--primary));
  }
`;

export interface V4HomeAccountSlotProps {
  state: AccountState;
  /** Viewport axis. Desktop has no tab bar, so the avatar is the You tab. */
  desktop?: boolean;
  onSignIn: () => void;
  onResend?: () => void;
  /** INTERIM gate (resendInterim.ts). When false (prod default) the Resend CTA
   *  becomes a static spam hint — see the flag's docblock. Prop, so tests drive
   *  both states; RESTORE = flip RESEND_CTA_ENABLED to true. */
  resendEnabled?: boolean;
  /**
   * The ANON avatar's click (signed-out WITH a board on desktop). A signed-in
   * avatar opens its account dropdown IN PLACE instead — it never routes through
   * here — so this is the "no identity yet, take me to the door" path, wired to
   * the sign-in modal. Kept as the reachability guard's tap target for that
   * cohort's avatar.
   */
  onOpenAccount?: () => void;
  /**
   * The nested account-menu controller (thea-web extension of #398). When the host
   * (`V4HomeDoor`) wires it, the anon-with-a-board desktop avatar opens the same
   * "Save your boards + Sign in" menu the board shows, instead of jumping straight
   * to the sign-in modal. Optional so legacy stories/tests keep the direct-door
   * fallback (`onOpenAccount`). Signed-in avatars are unaffected either way.
   */
  accountMenu?: {
    surface: AccountMenuSurface;
    onToggleMenu: () => void;
    onCloseMenu: () => void;
    onOpenCapture: () => void;
    onSignIn: () => void;
    onFixEmail?: (email: string) => void;
  };
}

export const V4HomeAccountSlot: React.FC<V4HomeAccountSlotProps> = ({
  state, desktop = false, onSignIn, onOpenAccount, accountMenu,
}) => {
  const slot = homeAccountSlot(state, { desktop });
  if (slot.kind === 'none') return null;

  if (slot.kind === 'avatar') {
    // Signed in → the shared avatar + in-place account dropdown (unchanged; the
    // one avatar every surface shows).
    if (state.signedIn) {
      return <V4AccountAvatar state={state} onAnonClick={() => undefined} testIdPrefix="v4-home" />;
    }
    // Anon WITH a board on desktop → the nested account MENU (Save your boards +
    // a wired Sign in in every anon state), when the host wires it. This extends
    // #398's board treatment to the homepage/People/Calendar/Gift-Guides shells.
    // Falls back to the direct sign-in door for legacy callers that pass no
    // controller (stories / older tests).
    if (accountMenu) {
      return (
        <V4AccountMenu
          state={state}
          surface={accountMenu.surface}
          onToggleMenu={accountMenu.onToggleMenu}
          onCloseMenu={accountMenu.onCloseMenu}
          onOpenCapture={accountMenu.onOpenCapture}
          onSignIn={accountMenu.onSignIn}
          menuId="v4-home-account-menu"
          testIdPrefix="v4-home"
        />
      );
    }
    return (
      <V4AccountAvatar
        state={state}
        onAnonClick={() => onOpenAccount?.()}
        testIdPrefix="v4-home"
      />
    );
  }

  return <Slot><SignInButton type="button" data-signin-entry="v4-homepage-header" onClick={onSignIn}>
    Log in
  </SignInButton></Slot>;
};

export default V4HomeAccountSlot;
